import type * as React from 'react';
import { cn } from '@/lib/utils';

export function Skeleton({ className, ...p }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-surface-2', className)} {...p} />;
}

export function Separator({ className, ...p }: React.HTMLAttributes<HTMLDivElement>) {
  return <div role="separator" className={cn('h-px w-full shrink-0 bg-border', className)} {...p} />;
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-label="carregando"
      className={cn('inline-block size-4 animate-spin rounded-pill border-2 border-current/25 border-t-current', className)}
    />
  );
}

/**
 * Chip de dado (manual §4): pílula mono 12px, fundo `--surface-2`, borda `--border`.
 * "Aceso" quando é o identificador que corroborou uma correlação.
 */
export function Chip({
  aceso = false,
  className,
  ...p
}: React.HTMLAttributes<HTMLSpanElement> & { aceso?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-2 py-0.5',
        'font-mono text-xs tabular-nums',
        aceso ? 'border-primary/40 text-primary' : 'border-border bg-surface-2 text-text-secondary',
        className,
      )}
      {...p}
    />
  );
}

/**
 * Barra de confiança (manual §4). Trilho `--surface-2`, preenchimento com o
 * gradiente frio→quente. SEMPRE acompanhada do valor em mono: cor sozinha não é
 * acessível. Baixa confiança nunca é escondida — é mostrada com honestidade.
 */
export function BarraConfianca({ valor, className }: { valor: number; className?: string }) {
  const pct = Math.max(0, Math.min(1, valor)) * 100;
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div
        role="meter"
        aria-valuenow={valor}
        aria-valuemin={0}
        aria-valuemax={1}
        className="h-1.5 flex-1 overflow-hidden rounded-pill bg-surface-2"
      >
        <div
          className="h-full rounded-pill"
          style={{ width: `${pct}%`, background: 'var(--conf-gradient)' }}
        />
      </div>
      <span className="tipo-dado text-text-secondary">{valor.toFixed(2)}</span>
    </div>
  );
}
